import { hasValue } from '@repo/utils';

import type { FactTrajectoryPointEntity, TPointData } from '../../../service/entities';
import type { TGraphTrajectoryView, TTrajectoryExcludeReason } from '../../../types/graph-trajectory';
import { sortTrajectoryPointsByMd } from '../../../utils/sort-trajectory-points-by-md';
import {
  MAIN_TRAJECTORY_LINE_WIDTH,
  REFERENCE_TRAJECTORY_LINE_WIDTH,
  getTrajectorySeriesColor,
} from '../../../utils/trajectory-line-colors';
import { formatGraphTrajectoryLegendLabel } from '../build-graph-trajectory-views';
import { getReferenceExcludeReasonForChart } from '../utils/chart-exclude-reason';
import { shiftPointForOverlay } from '../utils/trajectory-offset-deltas';

import type {
  Trajectory2dAxisField,
  Trajectory2dChartConfig,
  Trajectory2dChartTypeId,
} from './trajectory-2d-chart-configs';

/** Single-series chart point: [x, y] */
export type Trajectory2dChartPoint = [x: number, y: number];

export type Trajectory2dSeriesData = {
  trajectoryId: number;
  trajectoryTitle: string;
  legendLabel: string;
  field: keyof TPointData;
  labelKey: string;
  axisIndex: number;
  color: string;
  lineWidth: number;
  data: Trajectory2dChartPoint[];
};

export type Trajectory2dExcludedSeries = {
  trajectoryId: number;
  legendLabel: string;
  reason: TTrajectoryExcludeReason;
};

export type Trajectory2dMappedChart = {
  series: Trajectory2dSeriesData[];
  excluded: Trajectory2dExcludedSeries[];
  yInvert: boolean;
  yLabelKey: string;
};

export type Trajectory2dChartMappingSpec = Pick<Trajectory2dChartConfig, 'id' | 'xFields' | 'yField'>;

export type Trajectory2dMapOptions = {
  activeAziVertSectDeg: number | null;
  activeLegendLabel: string;
};

/** Charts that must not show reference trajectories (ТЗ 2.4). */
const MAIN_ONLY_CHART_IDS: ReadonlySet<Trajectory2dChartTypeId> = new Set(['incAziMd', 'dlsMd']);

/** Charts that use NS/WE-style horizontal overlay (wellhead deltas). */
const HORIZONTAL_OVERLAY_FIELDS: ReadonlySet<keyof TPointData> = new Set(['ns', 'we', 'vsec']);

/** Charts that align TVD via TVDSS delta. */
const VERTICAL_OVERLAY_FIELDS: ReadonlySet<keyof TPointData> = new Set(['tvd']);

function chartNeedsHorizontalOverlay(config: Trajectory2dChartMappingSpec): boolean {
  return (
    config.xFields.some((field) => HORIZONTAL_OVERLAY_FIELDS.has(field.field)) ||
    HORIZONTAL_OVERLAY_FIELDS.has(config.yField.field)
  );
}

function chartNeedsVerticalOverlay(config: Trajectory2dChartMappingSpec): boolean {
  return (
    config.xFields.some((field) => VERTICAL_OVERLAY_FIELDS.has(field.field)) ||
    VERTICAL_OVERLAY_FIELDS.has(config.yField.field)
  );
}

function chartNeedsCommonVsec(config: Trajectory2dChartMappingSpec): boolean {
  return config.xFields.some((field) => field.field === 'vsec') || config.yField.field === 'vsec';
}

function getChartOverlayRequirements(config: Trajectory2dChartMappingSpec) {
  return {
    needsHorizontal: chartNeedsHorizontalOverlay(config) || chartNeedsCommonVsec(config),
    needsVertical: chartNeedsVerticalOverlay(config),
  };
}

function readShiftedField(
  point: FactTrajectoryPointEntity,
  field: keyof TPointData,
  trajectory: TGraphTrajectoryView,
  config: Trajectory2dChartMappingSpec,
  activeAziVertSectDeg: number | null,
): number | null {
  const needsHorizontal = chartNeedsHorizontalOverlay(config);
  const needsVertical = chartNeedsVerticalOverlay(config);
  const useCommonVsec = chartNeedsCommonVsec(config);

  const shifted = shiftPointForOverlay(point.data, trajectory.deltas, {
    applyHorizontal: needsHorizontal && trajectory.role === 'reference',
    applyVertical: needsVertical && trajectory.role === 'reference',
    commonAziVertSectDeg:
      useCommonVsec && (trajectory.role === 'reference' || hasValue(activeAziVertSectDeg))
        ? activeAziVertSectDeg
        : null,
  });

  switch (field) {
    case 'ns':
      return shifted.ns;
    case 'we':
      return shifted.we;
    case 'tvd':
      return shifted.tvd;
    case 'vsec':
      return shifted.vsec;
    default: {
      const value = point.data[field];
      return typeof value === 'number' ? value : null;
    }
  }
}

function mapSeriesData(
  trajectory: TGraphTrajectoryView,
  xField: Trajectory2dAxisField,
  yField: Trajectory2dChartConfig['yField'],
  config: Trajectory2dChartMappingSpec,
  activeAziVertSectDeg: number | null,
): Trajectory2dChartPoint[] {
  return sortTrajectoryPointsByMd(trajectory.entity.points)
    .map((point) => {
      const x = readShiftedField(point, xField.field, trajectory, config, activeAziVertSectDeg);
      const y = readShiftedField(point, yField.field, trajectory, config, activeAziVertSectDeg);

      if (!hasValue(x) || !hasValue(y)) {
        return null;
      }

      return [x, y] satisfies Trajectory2dChartPoint;
    })
    .filter(hasValue);
}

export function mapTrajectoryPointsByConfig(
  trajectories: TGraphTrajectoryView[],
  config: Trajectory2dChartMappingSpec,
  options: Trajectory2dMapOptions = { activeAziVertSectDeg: null, activeLegendLabel: 'active' },
): Trajectory2dMappedChart {
  const { xFields, yField } = config;
  const requirements = getChartOverlayRequirements(config);
  const excluded: Trajectory2dExcludedSeries[] = [];
  const allowed: TGraphTrajectoryView[] = [];

  for (const trajectory of trajectories) {
    if (MAIN_ONLY_CHART_IDS.has(config.id)) {
      if (trajectory.role === 'main') {
        allowed.push(trajectory);
      }

      continue;
    }

    const reason = getReferenceExcludeReasonForChart(trajectory, requirements);

    if (reason) {
      excluded.push({
        trajectoryId: trajectory.id,
        legendLabel: formatGraphTrajectoryLegendLabel(trajectory, options.activeLegendLabel),
        reason,
      });
      continue;
    }

    allowed.push(trajectory);
  }

  const series = allowed.flatMap((trajectory) =>
    xFields.map((xField, seriesIndex) => ({
      trajectoryId: trajectory.id,
      trajectoryTitle: trajectory.title,
      legendLabel: formatGraphTrajectoryLegendLabel(trajectory, options.activeLegendLabel),
      field: xField.field,
      labelKey: xField.labelKey,
      axisIndex: xField.axisIndex ?? 0,
      color: getTrajectorySeriesColor(trajectory.id, xFields.length > 1 ? seriesIndex : 0),
      lineWidth: trajectory.role === 'main' ? MAIN_TRAJECTORY_LINE_WIDTH : REFERENCE_TRAJECTORY_LINE_WIDTH,
      data: mapSeriesData(trajectory, xField, yField, config, options.activeAziVertSectDeg),
    })),
  );

  return {
    series,
    excluded,
    yInvert: Boolean(yField.invert),
    yLabelKey: yField.labelKey,
  };
}
